import React, { useCallback } from "react";

import { faEllipsisH } from "@fortawesome/pro-solid-svg-icons/faEllipsisH";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Dropdown from "antd/es/dropdown";
import { MenuItemType, SubMenuType } from "antd/es/menu/hooks/useItems";
import cn from "classnames";
import { MenuInfo } from "rc-menu/lib/interface";

import ListWithOverflowItem from "@app/component/ListWithOverflowItem";
import RoundButton from "@app/component/RoundButton";

import styles from "./ActionsBar.module.scss";
import SubMenuActionButton from "./SubMenuActionButton";

export type ActionBarItemType = MenuItemType | SubMenuType;

interface Props {
  actions: ActionBarItemType[];
  className?: string;
  onClick?: (info: MenuInfo) => void;
}

const ActionsBar: React.FC<Props> = ({ actions, className, onClick }) => {
  const getParentElement = useCallback((trigger: HTMLElement) => trigger.parentElement!, []);
  const overflowRenderer = useCallback(
    (hiddenItemsCount: number) => (
      <Dropdown
        className={styles.menu}
        getPopupContainer={getParentElement}
        menu={{
          getPopupContainer: getParentElement,
          items: actions.slice(-hiddenItemsCount).map(({ icon, ...restProps }) => ({
            className: styles.menuItem,
            ...restProps,
          })),
          onClick,
        }}
        placement="bottomRight"
        trigger={["click"]}
      >
        <RoundButton
          className={styles.dropdownButton}
          leftIcon={<FontAwesomeIcon icon={faEllipsisH} />}
        />
      </Dropdown>
    ),
    [actions, getParentElement, onClick]
  );

  return (
    <div className={cn(styles.container, className)}>
      <ListWithOverflowItem className={styles.list} overflowRender={overflowRenderer}>
        {actions.map((action: ActionBarItemType) =>
          "children" in action && action.children ? (
            <SubMenuActionButton action={action} key={action.key} />
          ) : (
            <RoundButton
              className={styles.roundButton}
              disabled={action.disabled}
              key={action.key}
              leftIcon={action.icon}
              onClick={(e) =>
                action.onClick?.({
                  domEvent: e,
                  key: String(action.key),
                  keyPath: [String(action.key)],
                } as MenuInfo)
              }
            >
              {action?.label}
            </RoundButton>
          )
        )}
      </ListWithOverflowItem>
    </div>
  );
};

export default ActionsBar;
